'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Home, MessageCircle, User, Bell, Wallet, Search, Tag, ChevronLeft, ChevronRight, Briefcase, Heart, Building2, Truck, BookOpen, Hotel, Globe, Package, Car, Cog, Zap, Store, ShoppingBag, Shield, Radio, Download, LogIn, UserPlus, LayoutDashboard, FileText, Users, BarChart2, Settings, ChevronsUpDown, CheckCircle2 } from 'lucide-react';
import { useAuth, UserRole } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import NotificationBell from '@/components/NotificationBell';


interface SideNavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  href: string;
  badge?: number;
}

// ─── Public navigation (no auth required) ─────────────────────────────────────
const publicNavItems: SideNavItem[] = [
  { id: 'side-home', label: 'Home', icon: Home, href: '/' },
  { id: 'side-search', label: 'Search', icon: Search, href: '/search' },
  { id: 'side-classifieds', label: 'Classifieds', icon: Tag, href: '/classifieds' },
  { id: 'side-sweden', label: '🇸🇪 Sweden Store', icon: Globe, href: '/sweden-store' },
  { id: 'side-uk', label: '🇬🇧 UK Store', icon: Globe, href: '/uk-store' },
  { id: 'side-china', label: '🇨🇳 China Offers', icon: Package, href: '/china-offers' },
  { id: 'side-auto-parts', label: 'Auto Parts', icon: Car, href: '/auto-parts' },
  { id: 'side-machinery', label: 'Machinery', icon: Cog, href: '/machinery' },
  { id: 'side-jobs', label: 'Jobs', icon: Briefcase, href: '/jobs' },
  { id: 'side-hotels', label: 'Hotels', icon: Hotel, href: '/hotels' },
  { id: 'side-business', label: 'Business Directory', icon: Building2, href: '/business' },
  { id: 'side-delivery', label: 'Delivery', icon: Truck, href: '/delivery' },
  { id: 'side-education', label: 'Education', icon: BookOpen, href: '/education' },
  { id: 'side-property', label: 'Property', icon: Building2, href: '/property' },
  { id: 'side-pharmacy', label: 'Pharmacy', icon: ShoppingBag, href: '/pharmacy' },
  { id: 'side-beauty', label: 'Beauty & Fashion', icon: Heart, href: '/beauty-fashion' },
  { id: 'side-sports', label: 'Sports', icon: Zap, href: '/sports' },
  { id: 'side-live', label: '🔴 Live Listings', icon: Radio, href: '/live-listings' },
];

// ─── Role-specific private nav items ──────────────────────────────────────────
const roleNavItems: Record<UserRole, SideNavItem[]> = {
  buyer: [
    { id: 'priv-dashboard', label: 'My Dashboard', icon: LayoutDashboard, href: '/user-dashboard' },
    { id: 'priv-profile', label: 'Profile', icon: User, href: '/profile' },
    { id: 'priv-messages', label: 'Messages', icon: MessageCircle, href: '/chat', badge: 3 },
    { id: 'priv-notifications', label: 'Notifications', icon: Bell, href: '/notifications', badge: 7 },
    { id: 'priv-saved', label: 'Saved Items', icon: Heart, href: '/saved' },
    { id: 'priv-orders', label: 'Orders', icon: Package, href: '/bookings' },
    { id: 'priv-wallet', label: 'Wallet', icon: Wallet, href: '/wallet' },
    { id: 'priv-settings', label: 'Account Settings', icon: Settings, href: '/profile' },
  ],
  seller: [
    { id: 'priv-seller-dash', label: 'Seller Dashboard', icon: Store, href: '/seller-dashboard' },
    { id: 'priv-products', label: 'My Products', icon: Package, href: '/seller-dashboard' },
    { id: 'priv-listings', label: 'My Listings', icon: Tag, href: '/classifieds' },
    { id: 'priv-inquiries', label: 'Inquiries', icon: MessageCircle, href: '/seller-inquiries', badge: 2 },
    { id: 'priv-orders', label: 'My Orders', icon: ShoppingBag, href: '/bookings' },
    { id: 'priv-wallet', label: 'Payments', icon: Wallet, href: '/wallet' },
    { id: 'priv-messages', label: 'Messages', icon: MessageCircle, href: '/chat', badge: 3 },
    { id: 'priv-notifications', label: 'Notifications', icon: Bell, href: '/notifications' },
    { id: 'priv-settings', label: 'Seller Settings', icon: Settings, href: '/profile' },
  ],
  job_seeker: [
    { id: 'priv-js-dash', label: 'Job Seeker Dashboard', icon: LayoutDashboard, href: '/user-dashboard' },
    { id: 'priv-profile', label: 'My Profile', icon: User, href: '/profile' },
    { id: 'priv-cv', label: 'My CV', icon: FileText, href: '/user-dashboard' },
    { id: 'priv-saved-jobs', label: 'Saved Jobs', icon: Heart, href: '/saved' },
    { id: 'priv-applications', label: 'Applications', icon: Briefcase, href: '/user-dashboard' },
    { id: 'priv-messages', label: 'Messages', icon: MessageCircle, href: '/chat', badge: 3 },
    { id: 'priv-notifications', label: 'Job Alerts', icon: Bell, href: '/notifications' },
  ],
  employer: [
    { id: 'priv-emp-dash', label: 'Employer Dashboard', icon: LayoutDashboard, href: '/dashboard' },
    { id: 'priv-company', label: 'Company Profile', icon: Building2, href: '/profile' },
    { id: 'priv-post-job', label: 'Post Job', icon: Briefcase, href: '/jobs' },
    { id: 'priv-vacancies', label: 'My Vacancies', icon: Tag, href: '/dashboard' },
    { id: 'priv-applications', label: 'Applications', icon: Users, href: '/dashboard' },
    { id: 'priv-payments', label: 'Payments', icon: Wallet, href: '/wallet' },
    { id: 'priv-reports', label: 'Reports', icon: BarChart2, href: '/dashboard' },
  ],
  agent: [
    { id: 'priv-agent-dash', label: 'Agent Dashboard', icon: LayoutDashboard, href: '/dashboard' },
    { id: 'priv-agency', label: 'Agency Profile', icon: Building2, href: '/profile' },
    { id: 'priv-candidates', label: 'Candidates', icon: Users, href: '/dashboard' },
    { id: 'priv-jobs', label: 'Jobs', icon: Briefcase, href: '/jobs' },
    { id: 'priv-placements', label: 'Placements', icon: CheckCircle2, href: '/dashboard' },
    { id: 'priv-payments', label: 'Payments', icon: Wallet, href: '/wallet' },
    { id: 'priv-reports', label: 'Reports', icon: BarChart2, href: '/dashboard' },
  ],
  superadmin: [
    { id: 'priv-super-dash', label: 'Super Admin', icon: Shield, href: '/superadmin' },
    { id: 'priv-analytics', label: 'Analytics', icon: BarChart2, href: '/superadmin/analytics' },
    { id: 'priv-users', label: 'Users', icon: Users, href: '/superadmin' },
    { id: 'priv-modules', label: 'Modules', icon: LayoutDashboard, href: '/superadmin/modules' },
    { id: 'priv-wallet', label: 'Wallet', icon: Wallet, href: '/superadmin/wallet' },
    { id: 'priv-settings', label: 'Settings', icon: Settings, href: '/superadmin' },
  ],
};

const roleLabels: Record<UserRole, string> = {
  buyer: 'Buyer',
  seller: 'Seller',
  job_seeker: 'Job Seeker',
  employer: 'Employer',
  agent: 'Agent',
  superadmin: 'Super Admin',
};

export default function SidebarNav() {
  const [collapsed, setCollapsed] = useState(false);
  const [showRoleSwitcher, setShowRoleSwitcher] = useState(false);
  const [mounted, setMounted] = useState(false);
  const pathname = usePathname();
  const { user, activeRole, switchRole, getUserRoles } = useAuth();

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/' || pathname === '/home-screen';
    return pathname === href || pathname.startsWith(href + '/');
  };

  const userRoles = (mounted && user) ? getUserRoles() : [];
  const hasMultipleRoles = userRoles.length > 1;
  const privateItems = (mounted && user && activeRole) ? (roleNavItems[activeRole] || roleNavItems.buyer) : [];

  const renderNavItem = (item: SideNavItem) => {
    const active = isActive(item.href);
    const Icon = item.icon;
    return (
      <Link key={item.id} href={item.href}
        suppressHydrationWarning
        className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 relative group ${active ? 'bg-primary/10 text-primary border border-primary/20' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}>
        <Icon size={18} strokeWidth={active ? 2.5 : 2} className="flex-shrink-0" />
        {!collapsed && <span className="text-sm font-medium truncate">{item.label}</span>}
        {item.badge ? (
          <span className={`${collapsed ? 'absolute top-1 right-1' : 'ml-auto'} min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center`}
            style={{ background: 'var(--danger)', color: '#fff' }}>
            {item.badge}
          </span>
        ) : null}
        {collapsed && (
          <div className="absolute left-full ml-2 px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50"
            style={{ background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
            suppressHydrationWarning>
            {item.label}
          </div>
        )}
      </Link>
    );
  };

  return (
    <aside className={`hidden lg:flex flex-col h-screen sticky top-0 transition-all duration-300 ease-in-out flex-shrink-0 ${collapsed ? 'w-16' : 'w-56'}`}
      style={{ background: 'var(--secondary)', borderRight: '1px solid var(--border)' }}
      suppressHydrationWarning>

      {/* Logo */}
      <div className="flex items-center gap-2 px-4 py-4 border-b" style={{ borderColor: 'var(--border)' }} suppressHydrationWarning>
        <div className="w-8 h-8 rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center"
          style={{ background: 'rgba(0,200,83,0.1)', border: '1px solid rgba(0,200,83,0.3)' }}
          suppressHydrationWarning>
          <img src="/assets/china-ndogo-logo.svg" alt="China Ndogo Logo" width={28} height={28} />
        </div>
        {!collapsed && (
          <div className="flex flex-col" suppressHydrationWarning>
            <span className="text-sm font-extrabold tracking-tight text-gradient-green">CHINA NDOGO</span>
            <span className="text-[9px] font-700 tracking-widest" style={{ color: '#FFB300' }}>MKUBWA WAO</span>
          </div>
        )}
      </div>

      <nav className="flex-1 px-2 py-4 flex flex-col gap-1 overflow-y-auto scrollbar-hide" suppressHydrationWarning>

        {/* ── Authenticated: Role Switcher + Private Nav ── */}
        {mounted && user && (
          <>
            {/* Role switcher */}
            {!collapsed && (
              <div className="mb-2">
                <button
                  onClick={() => setShowRoleSwitcher(!showRoleSwitcher)}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-xl transition-all"
                  style={{ background: 'rgba(0,200,83,0.08)', border: '1px solid rgba(0,200,83,0.2)' }}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: 'var(--primary)' }} />
                    <span className="text-xs font-700" style={{ color: 'var(--primary)' }}>
                      {roleLabels[activeRole] || 'Buyer'}
                    </span>
                  </div>
                  {hasMultipleRoles && <ChevronsUpDown size={12} style={{ color: 'var(--muted-foreground)' }} />}
                </button>

                {showRoleSwitcher && hasMultipleRoles && (
                  <div className="mt-1 rounded-xl overflow-hidden" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                    <div className="px-3 py-1.5 text-[9px] font-800 tracking-widest" style={{ color: 'var(--muted-foreground)' }}>SWITCH WORKSPACE</div>
                    {userRoles.map((role) => (
                      <button key={role} onClick={() => { switchRole(role); setShowRoleSwitcher(false); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-600 transition-all hover:bg-white/5"
                        style={{ color: role === activeRole ? 'var(--primary)' : 'var(--foreground)' }}>
                        {role === activeRole && <CheckCircle2 size={12} style={{ color: 'var(--primary)' }} />}
                        {role !== activeRole && <div className="w-3" />}
                        {roleLabels[role]}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Private nav items for active role */}
            <div className="mb-2">
              {!collapsed && <div className="px-3 py-1 text-[9px] font-800 tracking-widest" style={{ color: 'var(--muted-foreground)' }}>MY WORKSPACE</div>}
              {privateItems.map(renderNavItem)}
            </div>

            {/* Divider */}
            <div className="my-2 mx-3 border-t" style={{ borderColor: 'var(--border)' }} />
            {!collapsed && <div className="px-3 py-1 text-[9px] font-800 tracking-widest" style={{ color: 'var(--muted-foreground)' }}>BROWSE</div>}
          </>
        )}

        {/* ── Public nav items (always visible) ── */}
        {publicNavItems.map(renderNavItem)}

        {/* ── Not logged in: Sign In / Create Account ── */}
        {(!mounted || !user) && (
          <>
            <div className="my-2 mx-3 border-t" style={{ borderColor: 'var(--border)' }} />
            <Link href="/login"
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 text-muted-foreground hover:bg-muted hover:text-foreground">
              <LogIn size={18} strokeWidth={2} className="flex-shrink-0" />
              {!collapsed && <span className="text-sm font-medium">Sign In</span>}
            </Link>
            <Link href="/onboarding"
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150"
              style={{ background: 'rgba(0,200,83,0.1)', color: 'var(--primary)', border: '1px solid rgba(0,200,83,0.2)' }}>
              <UserPlus size={18} strokeWidth={2} className="flex-shrink-0" />
              {!collapsed && <span className="text-sm font-medium">Create Account</span>}
            </Link>
          </>
        )}
      </nav>

      {/* Footer: Install App + Collapse */}
      <div className="p-3 border-t flex flex-col gap-1" style={{ borderColor: 'var(--border)' }} suppressHydrationWarning>
        {/* Notification Bell (authenticated only) */}
        {mounted && user && !collapsed && (
          <div className="flex items-center justify-between px-3 py-2 mb-1">
            <span className="text-xs font-600" style={{ color: 'var(--muted-foreground)' }}>Notifications</span>
            <NotificationBell />
          </div>
        )}
        {mounted && user && collapsed && (
          <div className="flex justify-center mb-1">
            <NotificationBell />
          </div>
        )}
        {!collapsed && (
          <Link href="/onboarding?install=1"
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-600 transition-all hover:bg-muted"
            style={{ color: 'var(--muted-foreground)' }}>
            <Download size={14} />
            <span>Install App</span>
          </Link>
        )}
        <button onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl transition-all duration-150 text-muted-foreground hover:bg-muted hover:text-foreground">
          {collapsed ? <ChevronRight size={16} /> : <><ChevronLeft size={16} /><span className="text-xs font-medium">Collapse</span></>}
        </button>
      </div>
    </aside>
  );
}